/**
 * BridgeDetail — bridge wave-2 item 11: the selection popover.
 *
 * PRIOR-ART ATTESTATION: none_exists (new component). Pattern-borrowed, NOT
 * forked: the AnimatePresence-guarded mount + reduced-motion plain-div branch
 * mirrors AssemblyLine's motion.div/div split (components/fleet/
 * AssemblyLine.tsx), and the body text reuses the SAME sessions payload
 * FleetHud already polled — this component issues ZERO network requests.
 *
 * CONTRACT (plan docs/plans/2026-08-12-hud-bridge-wave2-plan.md §6 item 11):
 * - Opens ONLY from a user gesture — BridgeCore lifts selectedInstrument out
 *   of the constellation node / stage arc click handlers. Nothing here (or
 *   upstream) auto-opens it: no effect, poll, or timer sets the selection.
 * - Closes on Esc keydown, on pointerdown outside the panel, or the × button.
 * - RED LINE (verbatim): the body is the session's `results` string
 *   (server-trimmed to 1200 chars) or its `logs` tail (server-trimmed to the
 *   last 6 lines) rendered EXACTLY as fetched — no summarisation, no
 *   synthesis. A session with neither (or sessions unavailable) renders the
 *   static empty-state label; it never fabricates content.
 * - RED LINE (reduced motion): a plain div — instant appear, instant
 *   unmount, zero animation frames. This is the JS half of the collapse
 *   (jsdom/tests never evaluate the CSS media query).
 *
 * House tokens only (bg-elevated / border-hairline / text-text-*) — no raw
 * hex, no hardcoded colours.
 */
import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FleetSession } from '../../services/fleetHudApi';
import { STAGE_AGENTS } from '../fleet/AssemblyLine';
import { parseTs } from '../fleet/fleetTime';
import { filterRunSessions } from './agentOrbit';

/** The lifted selection — owned by BridgeCore, mirrored by FleetHud for the
    spotlight class. kind 'agent' → id is an agent_type; kind 'stage' → id is
    a stage name (REACTOR_STAGES and ASSEMBLY_STAGES share the same six). */
export interface SelectedInstrument {
  kind: 'agent' | 'stage';
  id: string;
}

interface BridgeDetailProps {
  /** null = closed. The popover renders only while a selection is open. */
  selection: SelectedInstrument | null;
  /** Anchor as percentages (0–100) of the bridge-core wrapper, computed by
      BridgeCore from the same polar() geometry the instruments use. */
  anchor: { x: number; y: number } | null;
  /** The active run's sessions — ALREADY fetched by FleetHud's poll. */
  sessions?: FleetSession[];
  activeRunId?: string | null;
  /** Reduced-motion flag, passed DOWN from BridgeCore's live matchMedia hook
      — deliberately NOT framer-motion's useReducedMotion, which caches the
      preference in a module-level singleton on first call (untestable and
      unresponsive to changes). */
  reducedMotion?: boolean;
  onClose: () => void;
}

/** Most-recent-first recency key, same convention as AssemblyLine. */
function recency(s: FleetSession): number {
  return parseTs(s.started_at) ?? parseTs(s.updated_at) ?? 0;
}

/** Static empty state — an honest label, never fabricated content. */
const EMPTY_BODY = 'no session output recorded';

/**
 * VERBATIM extraction (RED LINE: no summarisation). `results` is the
 * backend-trimmed result string; `logs` the backend-trimmed tail lines. The
 * two field paths differ per agent kind (some agents write results, some
 * only logs) — both are read, results first, so no agent kind renders an
 * empty body while holding real output.
 */
export function sessionVerbatimText(session: FleetSession | undefined): string {
  if (!session) return EMPTY_BODY;
  if (typeof session.results === 'string' && session.results.trim().length > 0) {
    return session.results;
  }
  if (Array.isArray(session.logs) && session.logs.length > 0) {
    return session.logs.join('\n');
  }
  return EMPTY_BODY;
}

/** The session this selection shows: the most recent session of the agent,
    or (stage kind) the most recent session among the stage's mapped agents. */
function resolveSession(
  selection: SelectedInstrument,
  sessions: FleetSession[] | undefined,
  activeRunId: string | null | undefined
): FleetSession | undefined {
  const run = filterRunSessions(sessions, activeRunId);
  if (!run) return undefined;
  const candidates =
    selection.kind === 'agent'
      ? run.filter((s) => s.agent_type === selection.id)
      : run.filter((s) => {
          const agents = (STAGE_AGENTS as Record<string, readonly string[]>)[selection.id];
          return !!s.agent_type && !!agents && agents.includes(s.agent_type);
        });
  return candidates.sort((a, b) => recency(b) - recency(a))[0];
}

const PANEL_CLS = 'absolute z-30 w-64 border border-hairline bg-elevated shadow-lg';

export default function BridgeDetail({
  selection,
  anchor,
  sessions,
  activeRunId,
  reducedMotion = false,
  onClose,
}: BridgeDetailProps) {
  const reduced = reducedMotion;
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Close affordances — bound only while open. Esc keydown anywhere; a
  // pointerdown outside the panel (clicking another instrument closes first,
  // then that instrument's own click handler re-opens with the new
  // selection — gesture ordering makes the switch seamless).
  useEffect(() => {
    if (!selection) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onPointerDown = (e: Event) => {
      const panel = panelRef.current;
      if (panel && e.target instanceof Node && !panel.contains(e.target)) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [selection, onClose]);

  const session = selection ? resolveSession(selection, sessions, activeRunId) : undefined;

  return (
    <AnimatePresence>
      {selection !== null &&
        anchor !== null &&
        (reduced ? (
          /* Reduced motion: plain div — display block, no transform/opacity
             animation frame, instant mount AND instant unmount. */
          <div
            key="bridge-detail"
            ref={panelRef}
            role="dialog"
            aria-label={`${selection.kind} detail: ${selection.id}`}
            className={PANEL_CLS}
            style={{ left: `${anchor.x}%`, top: `${anchor.y}%`, marginTop: '-12px' }}
            data-testid="bridge-detail"
            data-kind={selection.kind}
          >
            <PanelBody selection={selection} session={session} onClose={onClose} />
          </div>
        ) : (
          /* Gesture-sourced entrance/exit, opacity only — never positional
             motion (the anchor is fixed geometry). Exit is 120ms so a closed
             popover is out of the DOM well inside the 400ms phantom window. */
          <motion.div
            key="bridge-detail"
            ref={panelRef}
            role="dialog"
            aria-label={`${selection.kind} detail: ${selection.id}`}
            className={PANEL_CLS}
            style={{ left: `${anchor.x}%`, top: `${anchor.y}%`, marginTop: '-12px' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.12 } }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            data-testid="bridge-detail"
            data-kind={selection.kind}
          >
            <PanelBody selection={selection} session={session} onClose={onClose} />
          </motion.div>
        ))}
    </AnimatePresence>
  );
}

/** Panel content, shared by the motion and reduced-motion branches. */
function PanelBody({
  selection,
  session,
  onClose,
}: {
  selection: SelectedInstrument;
  session: FleetSession | undefined;
  onClose: () => void;
}) {
  return (
    <>
      <header className="flex items-center justify-between gap-2 border-b border-hairline px-3 py-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-muted">
          {selection.kind}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close detail"
          className="font-mono text-xs text-text-secondary hover:text-text-primary"
          data-testid="bridge-detail-close"
        >
          ×
        </button>
      </header>
      <p
        className="px-3 pt-2 font-mono text-xs font-semibold text-text-primary"
        data-testid="bridge-detail-title"
      >
        {selection.id}
      </p>
      <p className="px-3 font-mono text-[10px] uppercase text-text-muted">
        {session
          ? `${session.agent_type ?? selection.id} · ${session.status ?? (session.completed_at ? 'completed' : 'open')}`
          : selection.kind === 'agent'
            ? 'no session for this agent'
            : 'no session in this stage'}
      </p>
      {/* VERBATIM body — server-trimmed results/logs rendered exactly as
          fetched (pre-wrap preserves the log line breaks). */}
      <pre
        className="max-h-48 overflow-auto whitespace-pre-wrap px-3 py-2 font-mono text-[11px] leading-relaxed text-text-secondary"
        data-testid="bridge-detail-body"
      >
        {sessionVerbatimText(session)}
      </pre>
    </>
  );
}
